import React, { useMemo } from 'react';
import { parseAssistantMarkdown } from './parseAssistantMarkdown';

const INDENT_CLASS = ['', 'ml-4', 'ml-8'];
const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' };

const renderInline = (nodes, onNavigate, keyPrefix = 'n') =>
  (nodes || []).map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case 'bold':
        return <strong key={key} className="font-semibold">{renderInline(node.children, onNavigate, key)}</strong>;
      case 'italic':
        return <em key={key}>{renderInline(node.children, onNavigate, key)}</em>;
      case 'code':
        return (
          <code key={key} className="rounded bg-background/70 px-1 py-0.5 font-mono text-[0.85em]">
            {node.text}
          </code>
        );
      case 'link':
        if (node.kind === 'internal' && onNavigate) {
          return (
            <button
              key={key}
              type="button"
              onClick={() => onNavigate(node.path)}
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {renderInline(node.children, onNavigate, key)}
            </button>
          );
        }
        if (node.kind === 'external') {
          return (
            <a
              key={key}
              href={node.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:opacity-80 break-words"
            >
              {renderInline(node.children, onNavigate, key)}
            </a>
          );
        }
        return <React.Fragment key={key}>{renderInline(node.children, onNavigate, key)}</React.Fragment>;
      default:
        return <React.Fragment key={key}>{node.text}</React.Fragment>;
    }
  });

// Blocks to React elements; a quote renders its own blocks inside it.
const renderBlocks = (blocks, onNavigate, prefix = 'b') =>
  blocks.map((block, index) => {
    const key = `${prefix}-${index}`;
    switch (block.type) {
      case 'blockquote':
        return (
          <blockquote key={key} className="space-y-2 border-l-2 border-border pl-3 text-muted-foreground">
            {renderBlocks(block.blocks, onNavigate, key)}
          </blockquote>
        );
      case 'heading':
        return (
          <p
            key={key}
            role="heading"
            aria-level={Math.min(6, block.level + 2)}
            className={block.level <= 2 ? 'font-semibold text-[0.95rem] leading-snug' : 'font-semibold leading-snug'}
          >
            {renderInline(block.children, onNavigate, key)}
          </p>
        );
      case 'list': {
        const ListTag = block.kind === 'ordered' ? 'ol' : 'ul';
        return (
          <ListTag key={key} className={`space-y-1 pl-5 ${block.kind === 'ordered' ? 'list-decimal' : 'list-disc'}`}>
            {block.items.map((item, itemIndex) => (
              <li
                key={`${key}-${itemIndex}`}
                value={block.kind === 'ordered' ? item.number : undefined}
                className={INDENT_CLASS[item.indent] || ''}
              >
                {renderInline(item.children, onNavigate, `${key}-${itemIndex}`)}
              </li>
            ))}
          </ListTag>
        );
      }
      case 'codeblock':
        return (
          <pre key={key} className="overflow-x-auto rounded bg-background/70 p-2 font-mono text-xs whitespace-pre">
            {block.lines.join('\n')}
          </pre>
        );
      case 'rule':
        return <hr key={key} className="border-border" />;
      case 'table':
        // Scrolls sideways inside the bubble rather than squeezing a wide
        // roster table to one word per line.
        return (
          <div key={key} className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-border">
                  {block.header.map((cell, cellIndex) => (
                    <th
                      key={`${key}-h${cellIndex}`}
                      scope="col"
                      className={`px-2 py-1 font-semibold whitespace-nowrap ${ALIGN_CLASS[block.align[cellIndex]] || ''}`}
                    >
                      {renderInline(cell, onNavigate, `${key}-h${cellIndex}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${key}-r${rowIndex}`} className="border-b border-border/60 last:border-0">
                    {row.map((cell, cellIndex) => (
                      <td
                        key={`${key}-r${rowIndex}-${cellIndex}`}
                        className={`px-2 py-1 align-top ${ALIGN_CLASS[block.align[cellIndex]] || ''}`}
                      >
                        {renderInline(cell, onNavigate, `${key}-r${rowIndex}-${cellIndex}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return (
          <p key={key}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`${key}-l${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {renderInline(line, onNavigate, `${key}-l${lineIndex}`)}
              </React.Fragment>
            ))}
          </p>
        );
    }
  });

// An assistant reply, formatted. Built from React elements only — a reply can
// never inject HTML — and re-parsed as the text streams in.
const AssistantMarkdown = ({ text, onNavigate }) => {
  const blocks = useMemo(() => parseAssistantMarkdown(text), [text]);
  return <div className="space-y-2 break-words">{renderBlocks(blocks, onNavigate)}</div>;
};

export default AssistantMarkdown;
